import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';

const apiUrl = process.env.REACT_APP_API_HOST;

function toViewerUrl(link) {
  if (!link) return link;
  try {
    const url = new URL(link);
    url.pathname = url.pathname.replace(/\/(edit|pub)(\/.*)?$/, '/view');
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return link;
  }
}

// URL de embed do Google Slides para exibir a apresentação num iframe.
function toEmbedUrl(link) {
  if (!link) return null;
  const m = String(link).match(/\/presentation\/d\/([^/]+)/);
  if (!m) return null;
  return `https://docs.google.com/presentation/d/${m[1]}/embed?start=false&loop=false`;
}

// ── Word-level diff (LCS) — estilo GitHub ──
// Tokeniza mantendo espaços para reconstruir o texto fielmente.
function tokenize(text) {
  return (text || '').match(/\S+|\s+/g) || [];
}

// Retorna lista de ops: { t: 'eq' | 'del' | 'add', v }
//   a = padrão, b = seu material
//   'del' = está no padrão e não no seu · 'add' = está no seu e não no padrão
function wordDiff(aText, bText) {
  const a = tokenize(aText);
  const b = tokenize(bText);
  const n = a.length, m = b.length;

  // Guard contra textos muito grandes (LCS é O(n·m))
  if (n * m > 4_000_000) {
    return [
      ...a.map((v) => ({ t: 'del', v })),
      ...b.map((v) => ({ t: 'add', v })),
    ];
  }

  const dp = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const ops = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { ops.push({ t: 'eq', v: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { ops.push({ t: 'del', v: a[i] }); i++; }
    else { ops.push({ t: 'add', v: b[j] }); j++; }
  }
  while (i < n) ops.push({ t: 'del', v: a[i++] });
  while (j < m) ops.push({ t: 'add', v: b[j++] });
  return ops;
}

function hasDiff(ops) {
  return ops.some((o) => o.t !== 'eq');
}

// Renderiza um lado do diff. side='pad' mostra eq+del, side='fac' mostra eq+add.
function DiffText({ ops, side }) {
  const highlightType = side === 'pad' ? 'del' : 'add';
  const hlStyle = side === 'pad'
    ? { backgroundColor: '#cdefd7', color: '#0f5132', borderRadius: 3, padding: '0 1px' }
    : { backgroundColor: '#cfe2ff', color: '#0a3678', borderRadius: 3, padding: '0 1px' };

  return (
    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {ops.map((op, idx) => {
        if (op.t === 'eq') return <span key={idx}>{op.v}</span>;
        if (op.t === highlightType) return <mark key={idx} style={hlStyle}>{op.v}</mark>;
        return null;
      })}
    </span>
  );
}

function ContentField({ label, padTexto, facTexto }) {
  const pad = padTexto || '';
  const fac = facTexto || '';
  if (!pad && !fac) return null;

  const ops = wordDiff(pad, fac);
  const diferente = hasDiff(ops);

  return (
    <div style={{ marginBottom: '0.9rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#495057', textTransform: 'uppercase', letterSpacing: '0.02em' }}>{label}</span>
        {diferente
          ? <span style={{ fontSize: '0.68rem', padding: '0.05rem 0.4rem', borderRadius: 10, backgroundColor: '#fff3cd', color: '#664d03' }}>alterado</span>
          : <span style={{ fontSize: '0.68rem', padding: '0.05rem 0.4rem', borderRadius: 10, backgroundColor: '#e9ecef', color: '#6c757d' }}>igual</span>
        }
      </div>
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'stretch' }}>
        <div style={{ flex: 1, padding: '0.5rem 0.6rem', backgroundColor: '#f6fbf8', border: '1px solid #cfe8d8', borderRadius: 6, fontSize: '0.82rem', lineHeight: 1.5, color: '#212529' }}>
          {pad ? <DiffText ops={ops} side="pad" /> : <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>— sem conteúdo —</span>}
        </div>
        <div style={{ flex: 1, padding: '0.5rem 0.6rem', backgroundColor: '#f5f9ff', border: '1px solid #cfe0f8', borderRadius: 6, fontSize: '0.82rem', lineHeight: 1.5, color: '#212529' }}>
          {fac ? <DiffText ops={ops} side="fac" /> : <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>— sem conteúdo —</span>}
        </div>
      </div>
    </div>
  );
}

function ContentDiffPanel({ estado, padLink, facLink }) {
  if (!estado) return null;

  return (
    <div style={{ padding: '0.75rem 0.9rem', backgroundColor: '#fbfcfd', borderLeft: '3px solid #fd7e14', borderRight: '1px solid #e9ecef', borderBottom: '1px solid #e9ecef', borderRadius: '0 0 4px 4px' }}>
      {/* Conteúdo dos slides — é aqui que está a diferença real (padrão × seu) */}
      {(padLink || facLink) && (
        <div style={{ marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.4rem', fontSize: '0.72rem', fontWeight: 700 }}>
            <div style={{ flex: 1, color: '#155724' }}>📋 Slide do padrão</div>
            <div style={{ flex: 1, color: '#1e40af' }}>✏️ Seu slide</div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <div style={{ flex: 1 }}>
              {padLink
                ? <iframe title="Slide do padrão" src={padLink} style={{ width: '100%', height: 260, border: '1px solid #cfe8d8', borderRadius: 6 }} allowFullScreen />
                : <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e9ecef', borderRadius: 6, color: '#adb5bd', fontStyle: 'italic', fontSize: '0.8rem' }}>— sem slide —</div>}
            </div>
            <div style={{ flex: 1 }}>
              {facLink
                ? <iframe title="Seu slide" src={facLink} style={{ width: '100%', height: 260, border: '1px solid #cfe0f8', borderRadius: 6 }} allowFullScreen />
                : <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e9ecef', borderRadius: 6, color: '#adb5bd', fontStyle: 'italic', fontSize: '0.8rem' }}>— sem slide —</div>}
            </div>
          </div>
        </div>
      )}

      {estado.loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6c757d', fontSize: '0.82rem' }}>
          <Spinner animation="border" size="sm" /> Carregando conteúdo…
        </div>
      )}

      {!estado.loading && estado.erro && (
        <div style={{ fontSize: '0.82rem', color: '#842029' }}>{estado.erro}</div>
      )}

      {!estado.loading && !estado.erro && estado.data && (() => {
        const p = estado.data.padrao || {};
        const f = estado.data.facilitador || {};
        const temAlgo = (p.descobertaguiada || f.descobertaguiada || p.handson || f.handson);
        if (!temAlgo) {
          return <div style={{ fontSize: '0.82rem', color: '#6c757d', fontStyle: 'italic' }}>Sem conteúdo textual cadastrado para comparar. A diferença pode estar apenas na apresentação (slides).</div>;
        }
        return (
          <>
            <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.5rem', fontSize: '0.72rem', fontWeight: 700 }}>
              <div style={{ flex: 1, color: '#155724' }}>📋 Padrão</div>
              <div style={{ flex: 1, color: '#1e40af' }}>✏️ Meu material</div>
            </div>
            <ContentField label="Descoberta guiada" padTexto={p.descobertaguiada} facTexto={f.descobertaguiada} />
            <ContentField label="Hands-on" padTexto={p.handson} facTexto={f.handson} />
            <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: '#6c757d' }}>
              <mark style={{ backgroundColor: '#cdefd7', color: '#0f5132', borderRadius: 3, padding: '0 2px' }}>verde</mark> = texto do padrão ·{' '}
              <mark style={{ backgroundColor: '#cfe2ff', color: '#0a3678', borderRadius: 3, padding: '0 2px' }}>azul</mark> = texto do seu material
            </div>
          </>
        );
      })()}
    </div>
  );
}

// Aligns two ordered theme lists into diff rows
// row.type: 'equal' | 'moved' | 'content-diff' | 'moved-content-diff' | 'only-pad' | 'only-fac'
function buildDiffRows(temasFac, temasPad) {
  const facByNome = Object.fromEntries(temasFac.map((t, i) => [t.nome, { tema: t, pos: i }]));
  const rows = [];
  const seen = new Set();

  temasPad.forEach((padTema, padPos) => {
    seen.add(padTema.nome);
    const fac = facByNome[padTema.nome];
    if (fac) {
      const posEqual = padPos === fac.pos;
      const contentEqual = !fac.tema.desatualizado;
      const type =
        posEqual && contentEqual ? 'equal'
        : !posEqual && contentEqual ? 'moved'
        : posEqual ? 'content-diff'
        : 'moved-content-diff';
      rows.push({ type, padTema, facTema: fac.tema, padPos, facPos: fac.pos, padOriginalIdx: padPos });
    } else {
      rows.push({ type: 'only-pad', padTema, facTema: null, padPos, facPos: null, padOriginalIdx: padPos });
    }
  });

  temasFac.forEach((facTema, facPos) => {
    if (!seen.has(facTema.nome)) {
      rows.push({ type: 'only-fac', padTema: null, facTema, padPos: null, facPos, padOriginalIdx: null });
    }
  });

  return rows;
}

const DIFF_CFG = {
  equal:             { lBg: '#f8f9fa',  rBg: '#f8f9fa',  lBd: 'transparent', rBd: 'transparent', lM: null,                                                              rM: null },
  moved:             { lBg: '#fffbe6',  rBg: '#fffbe6',  lBd: '#ffc107',     rBd: '#ffc107',     lM: { sym: '⇄', c: '#856404', tip: 'Posição diferente do padrão' },     rM: { sym: '⇄', c: '#856404', tip: 'Posição diferente do padrão' } },
  'content-diff':    { lBg: '#fff4e6',  rBg: '#fff4e6',  lBd: '#fd7e14',     rBd: '#fd7e14',     lM: { sym: '≠',  c: '#843908', tip: 'O padrão tem uma versão mais recente deste tema' }, rM: { sym: '≠', c: '#843908', tip: 'Versão desatualizada em relação ao padrão' } },
  'moved-content-diff': { lBg: '#fff0d4', rBg: '#fff0d4', lBd: '#fd7e14',   rBd: '#fd7e14',     lM: { sym: '≠⇄', c: '#843908', tip: 'Posição e conteúdo diferentes do padrão' }, rM: { sym: '≠⇄', c: '#843908', tip: 'Posição e conteúdo diferentes do padrão' } },
  'only-pad':        { lBg: '#d6f0df',  rBg: '#f2f2f2',  lBd: '#28a745',     rBd: 'transparent', lM: { sym: '−',  c: '#155724', tip: 'Este tema está no padrão mas não no seu material' }, rM: null },
  'only-fac':        { lBg: '#f2f2f2',  rBg: '#dbeafe',  lBd: 'transparent', rBd: '#0d6efd',     lM: null,                                                              rM: { sym: '+', c: '#1e40af', tip: 'Tema que você tem mas que não está no padrão' } },
};

function Marker({ m }) {
  if (!m) return <span style={{ minWidth: '1.8rem', flexShrink: 0 }} />;
  return (
    <span
      title={m.tip}
      style={{ minWidth: '1.8rem', flexShrink: 0, fontWeight: 700, fontSize: '0.76rem', color: m.c, userSelect: 'none', cursor: 'default', fontFamily: 'monospace' }}
    >
      {m.sym}
    </span>
  );
}

function DiffRow({ row, diaIdx, pendencias, togglePendencia, salvando, conteudoState, onToggleConteudo }) {
  const { type, padTema, facTema, padPos, facPos, padOriginalIdx } = row;
  const cfg = DIFF_CFG[type];
  const posKey = padOriginalIdx !== null ? `${diaIdx}_${padOriginalIdx}` : null;
  const selecionado = !!(posKey && pendencias[posKey] !== undefined);
  const canCopy = type !== 'equal' && type !== 'only-fac' && posKey;
  // Botão de comparar conteúdo só aparece quando o padrão está mais novo (content-diff)
  // E o facilitador de fato editou a cópia. Cópia fiel/intocada não precisa de revisão
  // manual — para essa, o botão "Usar padrão" já resolve com segurança.
  const canDiffConteudo =
    (type === 'content-diff' || type === 'moved-content-diff') && padTema && facTema && facTema.editado;
  const conteudoAberto = !!(conteudoState && posKey && conteudoState[posKey]);

  const numSt = { color: '#9ca3af', fontSize: '0.76rem', minWidth: '1.4rem', flexShrink: 0, fontFamily: 'monospace' };

  const cell = (bg, bd) => ({
    flex: 1, display: 'flex', alignItems: 'center', gap: '0.3rem',
    padding: '0.38rem 0.55rem', backgroundColor: bg,
    borderLeft: `3px solid ${bd}`, minHeight: '2rem', fontSize: '0.87rem',
  });

  const btnBase = {
    fontSize: '0.72rem', padding: '0.15rem 0.45rem', borderRadius: 4,
    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
    lineHeight: '1.4', transition: 'all 0.15s',
  };

  return (
    <div style={{
      marginBottom: '2px', borderRadius: 4, overflow: 'hidden',
      border: selecionado ? '2px solid #198754' : '2px solid transparent',
    }}>
      <div style={{ display: 'flex' }}>
        {/* Padrão (esquerda) */}
        <div style={cell(cfg.lBg, cfg.lBd)}>
          <Marker m={cfg.lM} />
          {padTema ? (
            <>
              <span style={numSt}>{padPos + 1}.</span>
              {toViewerUrl(padTema.link)
                ? <a href={toViewerUrl(padTema.link)} target="_blank" rel="noopener noreferrer" style={{ color: '#155724', textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{padTema.nome}</a>
                : <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{padTema.nome}</span>
              }
            </>
          ) : (
            <span style={{ flex: 1, color: '#c0c7cf', fontStyle: 'italic', fontSize: '0.8rem' }}>— não está no padrão</span>
          )}
        </div>

        <div style={{ width: '1px', backgroundColor: '#dee2e6', flexShrink: 0 }} />

        {/* Meu material (direita) */}
        <div style={cell(cfg.rBg, cfg.rBd)}>
          <Marker m={cfg.rM} />
          {facTema ? (
            <>
              <span style={numSt}>{facPos + 1}.</span>
              {facTema.link
                ? <a href={facTema.link} target="_blank" rel="noopener noreferrer" style={{ color: '#1e40af', textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{facTema.nome}</a>
                : <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{facTema.nome}</span>
              }
              {type === 'only-fac' && (
                <span style={{ fontSize: '0.68rem', padding: '0.1rem 0.4rem', borderRadius: 4, backgroundColor: '#bfdbfe', color: '#1e40af', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  Personalizado
                </span>
              )}
              {canDiffConteudo && (
                <button
                  onClick={() => onToggleConteudo(posKey, padTema.id_versao, facTema.id_versao)}
                  title="Ver o que mudou no texto (descoberta guiada e hands-on)"
                  style={{
                    ...btnBase, marginLeft: 'auto',
                    border: `1px solid ${conteudoAberto ? '#fd7e14' : '#adb5bd'}`,
                    backgroundColor: conteudoAberto ? '#fff0e0' : 'transparent',
                    color: conteudoAberto ? '#843908' : '#6c757d',
                  }}
                >
                  {conteudoAberto ? '▲ Ocultar conteúdo' : '🔍 Comparar conteúdo'}
                </button>
              )}
              {canCopy && (
                <button
                  onClick={() => togglePendencia(posKey, padTema.id_versao, diaIdx)}
                  disabled={salvando}
                  title={selecionado ? 'Clique para desmarcar' : 'Substituir pelo tema do padrão'}
                  style={{
                    ...btnBase,
                    marginLeft: canDiffConteudo ? '0' : 'auto',
                    border: `1px solid ${selecionado ? '#198754' : '#6c757d'}`,
                    backgroundColor: selecionado ? '#198754' : 'transparent',
                    color: selecionado ? '#fff' : '#495057',
                  }}
                >
                  {selecionado ? '✓' : '↩ Usar padrão'}
                </button>
              )}
            </>
          ) : type === 'only-pad' ? (
            <>
              <span style={{ flex: 1, color: '#c0c7cf', fontStyle: 'italic', fontSize: '0.8rem' }}>— você não tem este tema</span>
              {posKey && (
                <button
                  onClick={() => togglePendencia(posKey, padTema.id_versao, diaIdx)}
                  disabled={salvando}
                  title={selecionado ? 'Clique para desmarcar' : 'Adicionar este tema ao seu material'}
                  style={{
                    ...btnBase, marginLeft: 'auto',
                    border: `1px solid ${selecionado ? '#198754' : '#28a745'}`,
                    backgroundColor: selecionado ? '#198754' : 'transparent',
                    color: selecionado ? '#fff' : '#28a745',
                  }}
                >
                  {selecionado ? '✓' : '+ Adicionar'}
                </button>
              )}
            </>
          ) : null}
        </div>
      </div>

      {conteudoAberto && (
        <ContentDiffPanel
          estado={conteudoState[posKey]}
          padLink={toEmbedUrl(padTema && padTema.link)}
          facLink={toEmbedUrl(facTema && facTema.link)}
        />
      )}
    </div>
  );
}

function Pill({ bg, bd, color, children }) {
  return (
    <span style={{ fontSize: '0.76rem', padding: '0.2rem 0.55rem', borderRadius: 12, backgroundColor: bg, color, border: `1px solid ${bd}`, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function LegItem({ bg, bd, lbl }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
      <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: bg, border: `1px solid ${bd}`, display: 'inline-block', flexShrink: 0 }} />
      {lbl}
    </span>
  );
}

function ModalComparacaoSequencia({ show, onClose, modulo, email, onAplicado }) {
  const [diasFacilitador, setDiasFacilitador] = useState([]);
  const [diasPadrao, setDiasPadrao]           = useState([]);
  const [loadingDados, setLoadingDados]       = useState(false);
  const [salvando, setSalvando]               = useState(false);
  const [erro, setErro]                       = useState(null);
  const [pendencias, setPendencias]           = useState({});
  const [soMostrarDiferencas, setSoMostrarDiferencas] = useState(false);

  // Estado dos painéis de diff de conteúdo, keyed por posKey ("diaIdx_padIdx")
  //   ausente/undefined = fechado · { loading, erro, data } = aberto
  const [conteudoState, setConteudoState] = useState({});

  const fetchComparacao = useCallback(() => {
    if (!modulo || !email) return;
    setLoadingDados(true);
    setErro(null);
    fetch(
      `${apiUrl}/api/comparacaosequencia?modulo=${encodeURIComponent(modulo)}&email=${encodeURIComponent(email)}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } }
    )
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        if (data.erro) setErro(data.erro);
        else { setDiasFacilitador(data.facilitador || []); setDiasPadrao(data.padrao || []); }
      })
      .catch(() => setErro('Erro ao buscar as sequências. Verifique sua conexão.'))
      .finally(() => setLoadingDados(false));
  }, [modulo, email]);

  useEffect(() => {
    if (!show) return;
    setDiasFacilitador([]); setDiasPadrao([]); setPendencias({}); setErro(null); setConteudoState({});
    fetchComparacao();
  }, [show, fetchComparacao]);

  const togglePendencia = (key, idVersaoPadrao, diaIndex) => {
    setPendencias(prev => {
      if (prev[key] !== undefined) { const n = { ...prev }; delete n[key]; return n; }
      return { ...prev, [key]: { id_versao_padrao: idVersaoPadrao, dia_index: diaIndex } };
    });
  };

  // Abre/fecha o painel de conteúdo e busca os textos sob demanda.
  const toggleConteudo = (posKey, idVersaoPad, idVersaoFac) => {
    // Já aberto → fecha.
    if (conteudoState[posKey]) {
      setConteudoState(prev => { const n = { ...prev }; delete n[posKey]; return n; });
      return;
    }
    // Fechado → abre em loading e busca os textos.
    setConteudoState(prev => ({ ...prev, [posKey]: { loading: true, erro: null, data: null } }));
    fetch(
      `${apiUrl}/api/conteudoversao?id_versao_fac=${encodeURIComponent(idVersaoFac)}&id_versao_padrao=${encodeURIComponent(idVersaoPad)}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } }
    )
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      // O guard `p[posKey] ?` evita gravar caso o usuário feche o painel antes da resposta.
      .then(data => setConteudoState(p => (p[posKey] ? { ...p, [posKey]: { loading: false, erro: data.erro || null, data: data.erro ? null : data } } : p)))
      .catch(() => setConteudoState(p => (p[posKey] ? { ...p, [posKey]: { loading: false, erro: 'Erro ao carregar o conteúdo.', data: null } } : p)));
  };

  const handleSalvar = () => {
    const copias = Object.values(pendencias);
    if (!copias.length) return;
    setSalvando(true);
    fetch(`${apiUrl}/api/copiarversaopadrao`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ modulo, email, copias }),
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        if (data.erro) setErro(data.erro);
        else { setDiasFacilitador(data.facilitador || []); setPendencias({}); setConteudoState({}); if (onAplicado) onAplicado(); }
      })
      .catch(() => setErro('Erro ao salvar. Tente novamente.'))
      .finally(() => setSalvando(false));
  };

  const handleFechar = () => { setPendencias({}); setConteudoState({}); onClose(); };

  const totalDias    = Math.max(diasFacilitador.length, diasPadrao.length);
  const totalPendente = Object.keys(pendencias).length;

  // Pre-build all diff rows + global counters
  const allRows = Array.from({ length: totalDias }, (_, i) =>
    buildDiffRows(diasFacilitador[i] || [], diasPadrao[i] || [])
  );
  let nIguais = 0, nMovidos = 0, nConteudo = 0, nMovidosEConteudo = 0, nSoPad = 0, nSoSeu = 0;
  allRows.flat().forEach(r => {
    if      (r.type === 'equal')             nIguais++;
    else if (r.type === 'moved')             nMovidos++;
    else if (r.type === 'content-diff')      nConteudo++;
    else if (r.type === 'moved-content-diff') nMovidosEConteudo++;
    else if (r.type === 'only-pad')          nSoPad++;
    else if (r.type === 'only-fac')          nSoSeu++;
  });
  const totalDiffs = nMovidos + nConteudo + nMovidosEConteudo + nSoPad + nSoSeu;

  return (
    <Modal show={show} onHide={handleFechar} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem' }}>Comparação de Módulos — {modulo}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '1.25rem' }}>
        {loadingDados && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Spinner animation="border" variant="primary" />
            <p style={{ marginTop: '1rem', color: '#6c757d' }}>Carregando módulos…</p>
          </div>
        )}

        {!loadingDados && erro && (
          <div style={{ backgroundColor: '#f8d7da', border: '1px solid #dc3545', borderRadius: 6, padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.88rem', color: '#842029', textAlign: 'center' }}>
            {erro}
          </div>
        )}

        {!loadingDados && !erro && totalDias === 0 && (
          <div style={{ color: '#6c757d', textAlign: 'center', padding: '2rem' }}>
            Nenhuma sequência encontrada para este módulo.
          </div>
        )}

        {!loadingDados && !erro && totalDias > 0 && (
          <>
            {/* ── Resumo global ── */}
            {totalDiffs === 0 ? (
              <div style={{ backgroundColor: '#d1e7dd', border: '1px solid #a3cfbb', borderRadius: 6, padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.88rem', color: '#0f5132' }}>
                ✓ Seu material está idêntico ao padrão — nenhuma diferença encontrada.
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem', alignItems: 'center' }}>
                <span style={{ fontSize: '0.82rem', color: '#495057', fontWeight: 600, marginRight: '0.2rem' }}>Resumo:</span>
                {nSoPad > 0            && <Pill bg="#d6f0df" bd="#a3cfbb" color="#155724">{`− ${nSoPad} só no padrão`}</Pill>}
                {nSoSeu > 0            && <Pill bg="#dbeafe" bd="#93c5fd" color="#1e40af">{`+ ${nSoSeu} só no seu`}</Pill>}
                {nMovidos > 0          && <Pill bg="#fffbe6" bd="#ffc107" color="#856404">{`⇄ ${nMovidos} posição diferente`}</Pill>}
                {nConteudo > 0         && <Pill bg="#fff4e6" bd="#fd7e14" color="#843908">{`≠ ${nConteudo} conteúdo desatualizado`}</Pill>}
                {nMovidosEConteudo > 0 && <Pill bg="#fff0d4" bd="#fd7e14" color="#843908">{`≠⇄ ${nMovidosEConteudo} posição e conteúdo`}</Pill>}
                {nIguais > 0           && <Pill bg="#f8f9fa" bd="#dee2e6" color="#6c757d">{`✓ ${nIguais} iguais`}</Pill>}
              </div>
            )}

            {/* ── Banner de pendências ── */}
            {totalPendente > 0 && (
              <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.88rem', color: '#664d03' }}>
                {totalPendente} tema{totalPendente > 1 ? 's' : ''} selecionado{totalPendente > 1 ? 's' : ''} para cópia.
                Clique em <strong>Salvar</strong> para aplicar ou feche para descartar.
              </div>
            )}

            {/* ── Filtro + legenda ── */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <button
                onClick={() => setSoMostrarDiferencas(v => !v)}
                style={{
                  fontSize: '0.8rem', padding: '0.25rem 0.75rem', borderRadius: 20,
                  cursor: 'pointer', border: '1px solid', transition: 'all 0.15s', whiteSpace: 'nowrap',
                  borderColor: soMostrarDiferencas ? '#0d6efd' : '#ced4da',
                  backgroundColor: soMostrarDiferencas ? '#e7f1ff' : '#fff',
                  color: soMostrarDiferencas ? '#0d6efd' : '#6c757d',
                }}
              >
                {soMostrarDiferencas ? '●' : '○'} Mostrar só diferenças
              </button>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', fontSize: '0.74rem', color: '#6c757d' }}>
                <span>Legenda:</span>
                <LegItem bg="#d6f0df" bd="#a3cfbb" lbl="− Só no padrão" />
                <LegItem bg="#dbeafe" bd="#93c5fd" lbl="+ Só no seu" />
                <LegItem bg="#fffbe6" bd="#ffc107" lbl="⇄ Posição diferente" />
                <LegItem bg="#fff4e6" bd="#fd7e14" lbl="≠ Conteúdo desatualizado" />
              </div>
            </div>

            {/* ── Cabeçalhos das colunas ── */}
            <div style={{ display: 'flex', marginBottom: '0.5rem', borderRadius: '6px 6px 0 0', overflow: 'hidden', border: '1px solid #dee2e6' }}>
              <div style={{ flex: 1, padding: '0.5rem 0.75rem', backgroundColor: '#f0faf3', borderBottom: '3px solid #28a745' }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#155724' }}>📋 Material padrão</div>
                <div style={{ fontSize: '0.72rem', color: '#6c757d', marginTop: '0.1rem' }}>A referência oficial — o que foi definido para todos os facilitadores</div>
              </div>
              <div style={{ width: '1px', backgroundColor: '#dee2e6' }} />
              <div style={{ flex: 1, padding: '0.5rem 0.75rem', backgroundColor: '#eff6ff', borderBottom: '3px solid #0d6efd' }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e40af' }}>✏️ Meu material</div>
                <div style={{ fontSize: '0.72rem', color: '#6c757d', marginTop: '0.1rem' }}>Suas personalizações — o que você usa atualmente nas suas aulas</div>
              </div>
            </div>

            {/* ── Dias ── */}
            {allRows.map((rows, diaIdx) => {
              const visible = soMostrarDiferencas ? rows.filter(r => r.type !== 'equal') : rows;
              if (soMostrarDiferencas && visible.length === 0) return null;
              const nDiffs = rows.filter(r => r.type !== 'equal').length;

              return (
                <div key={diaIdx} style={{ marginBottom: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.75rem 0 0.35rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#495057', borderLeft: '4px solid #0d6efd', paddingLeft: '0.5rem' }}>
                      Dia {diaIdx + 1}
                    </span>
                    {nDiffs === 0
                      ? <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.45rem', borderRadius: 10, backgroundColor: '#d1e7dd', color: '#0f5132' }}>✓ Idêntico</span>
                      : <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.45rem', borderRadius: 10, backgroundColor: '#fff3cd', color: '#664d03' }}>{nDiffs} diferença{nDiffs > 1 ? 's' : ''}</span>
                    }
                  </div>
                  {visible.map((row, i) => (
                    <DiffRow
                      key={i}
                      row={row}
                      diaIdx={diaIdx}
                      pendencias={pendencias}
                      togglePendencia={togglePendencia}
                      salvando={salvando}
                      conteudoState={conteudoState}
                      onToggleConteudo={toggleConteudo}
                    />
                  ))}
                </div>
              );
            })}
          </>
        )}
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: 'space-between' }}>
        <Button variant="outline-secondary" onClick={handleFechar} disabled={salvando}>
          Fechar sem salvar
        </Button>
        <Button variant="success" onClick={handleSalvar} disabled={salvando || totalPendente === 0}>
          {salvando
            ? <><Spinner animation="border" size="sm" style={{ marginRight: '0.4rem' }} />Salvando…</>
            : `Salvar${totalPendente > 0 ? ` (${totalPendente})` : ''}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ModalComparacaoSequencia;

// Exportados para teste unitário.
export { wordDiff, buildDiffRows, tokenize };
